import { createEnumParam, useQueryParam, withDefault } from 'use-query-params'

import FileAddOutlinedIcon from '@ant-design/icons/FileAddOutlined'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Fade from '@mui/material/Fade'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import { RoleGuard } from '@shared/providers/src/MeProvider'
import { UserRole } from '@shared/utils'

import SOPsList, { useSOPsFilters } from './SOPsList'
import SOPsNotifications from './SOPsNotifications'
import { useSOPNotificationIndicator } from './SOPsNotifications/SOPsNotifications.hooks'

// The workspace's left panel: Documents / Updates tabs over a scrolling list, with the admin
// "Add" action on the right of the tab row. Selection is URL-driven so picking a row just
// updates filters and the right pane reacts.
export default function SOPsSidebar() {
  const [tab, setTab] = useTab()
  const [, updateFilters] = useSOPsFilters()
  const { data: hasUnreadSOPs } = useSOPNotificationIndicator()

  const isList = tab === 'list'

  return (
    <Stack sx={styles.root} data-testid="sops-sidebar">
      <Stack direction="row" sx={styles.header}>
        <Tabs value={tab} onChange={(e, value) => setTab(value)}>
          <Tab value="list" label="Documents" />
          <Tab
            value="updates"
            iconPosition="end"
            icon={hasUnreadSOPs ? <Chip label="!" color="error" variant="light" size="small" /> : null}
            label="Updates"
          />
        </Tabs>
        <RoleGuard allowed={[UserRole.Admin]}>
          <Fade in={isList} unmountOnExit>
            <Button
              variant="contained"
              size="small"
              endIcon={<FileAddOutlinedIcon />}
              onClick={() => updateFilters({ sops: 'create', sopId: undefined })}
              data-testid="sop-create"
            >
              Add
            </Button>
          </Fade>
        </RoleGuard>
      </Stack>

      <Stack sx={styles.body}>
        <Fade in appear key={tab} timeout={250}>
          <Box>{isList ? <SOPsList /> : <SOPsNotifications />}</Box>
        </Fade>
      </Stack>
    </Stack>
  )
}

const TabParam = createEnumParam(['list', 'updates'])
const useTab = () => useQueryParam('sops-tab', withDefault(TabParam, 'list'), { updateType: 'replaceIn' })

const styles = {
  root: {
    height: '100%',
    minHeight: 0,
  },
  header: {
    flex: '0 0 auto',
    px: 1.5,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
}
